
import {
  Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, Inject,
  ChangeDetectorRef, AfterViewInit, AfterViewChecked, OnDestroy, HostListener, Output, EventEmitter
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { CarouselDirection } from '@app/modules/carousel/entities/carousel-direction';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { GalleryDestination } from '@ux/components/gallery-destination/entities/gallery-destination';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { Banner } from '@shared/sliders/entities/slide-models';
import { SECTION_TYPE_NAME } from '@shared/home-sections/entities/section-type';
import { EventDataLayer } from '@avo/interface/trigger-props';
import { deeplinkClick } from '@home/mappers/home.mapper';
import { HomeDeeplinkType } from '@shared/home-sections/entities/home-data.response';
import { AVO_EVENTS } from '@avo/events/avo.events';

const INTERVAL = 7000;
const ONE_SECOND = 1000;

@Component({
  selector: 'app-gallery-destination',
  templateUrl: './gallery-destination.component.html',
  styleUrls: ['./gallery-destination.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class GalleryDestinationComponent implements AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() images: GalleryDestination[];
  @Input() height: number;
  @Input() source: EVENT_SOURCE | string;
  @Input() isMobile: boolean;
  @Input() salesChannel: string = null;
  @Input() hasWidgets: boolean;

  @Output() trackEvent: EventEmitter<EventDataLayer> = new EventEmitter<EventDataLayer>();

  @ViewChild('carouselContent') content: ElementRef;
  @ViewChild('dotsItem') dotsItem: ElementRef;

  private carouselDirection: CarouselDirection = CarouselDirection.Rigth;
  private subscriptions: Subscription[] = [];

  public dots: number[];
  public currentPosition: number = 0;
  public typeImageFlight: string = IMAGE_TYPE.FLIGHT;
  public dotPercentaje: number = 0;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.autoMove();
    }
  }

  ngAfterViewChecked(): void {
    if (this.isBrowser) {
      this.dots = Array(this.content.nativeElement.children.length).fill(0);
      this.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  @HostListener('scroll', ['$event']) onScroll(event: Event): void {
    if (this.isMobile) {
      this.currentPosition = Math.round(event.target['scrollLeft'] / event.target['clientWidth']);
    }
  }

  get showLeft(): boolean {
    return this.content.nativeElement.scrollLeft > 0;
  }

  get showRight(): boolean {
    const { clientWidth, scrollLeft, scrollWidth } = this.content.nativeElement;
    return scrollLeft + clientWidth < scrollWidth;
  }

  public moveSlider(position: number): void {
    if (position > this.currentPosition) {
      this.carouselDirection = CarouselDirection.Rigth;
    } else {
      this.carouselDirection = CarouselDirection.Left;
    }
    let currentPosition = this.currentPosition;
    while (currentPosition !== position) {
      if (this.carouselDirection === CarouselDirection.Rigth) {
        currentPosition++;
      } else currentPosition--;
      if (!this.content.nativeElement) return;
      if (currentPosition >= this.content.nativeElement.children.length) {
        position = currentPosition = 0;
      }
      this.scrollCarouselTo(this.content.nativeElement.children[currentPosition]?.offsetLeft, .2);
    }
    this.currentPosition = position;
  }

  public moveCarouselByDot(index: number): void {
    this.moveSlider(index);
  }

  private scrollCarouselTo(position: number, duration: number): void {
    this.content.nativeElement.scrollTo({
      left: position, top: top, behavior: 'smooth'
    });
  }

  private detectChanges(): void {
    this.changeDetector.detectChanges();
  }

  private autoMove(): void {
    let seconds = 0;
    this.dotPercentaje = 0;
    const timer$ = interval(ONE_SECOND);
    this.subscriptions.push(timer$.subscribe((sec) => {
      seconds++;
      this.dotPercentaje = ((seconds + 1) / (INTERVAL / ONE_SECOND)) * 100;
      if ((seconds * ONE_SECOND) === INTERVAL) {
        seconds = 0;
        this.dotPercentaje = 0;
        const newPosition = this.carouselDirection === CarouselDirection.Rigth ?
          this.currentPosition + 1 : this.currentPosition - 1;
        this.moveSlider(newPosition);
      }
    }));
  }

  public callDeeplink(banner: Banner, index: number): void {
    if (banner.deeplink) {
      deeplinkClick(banner?.deeplink, HomeDeeplinkType.RouterLink, this.router, this.salesChannel, this.source);
      const page_code = banner.deeplink.slice(banner.deeplink.lastIndexOf('/') + 1) || '';
      this.triggerAmplitudEvent(null, page_code, null, index + 1, SECTION_TYPE_NAME.Banner);
    }
  }

  public triggerAmplitudEvent(titleSection: string, titlePromo: string,
    indexSection: number, indexPromo: number, typeWidget: string): void {
    this.trackEvent.emit({
      eventType: AVO_EVENTS.SELECT_BANNER,
      eventProperties: {
        SOURCE: this.source,
        TITLE_PROMO: titlePromo,
        TITLE_SECTION: titleSection,
        INDEX_SECTION: indexSection,
        INDEX_PROMO: indexPromo,
        TYPE_WIDGET: typeWidget,
      }
    });
  }
}
